import { Component, Inject, OnDestroy, OnInit, ChangeDetectorRef, ViewChild, AfterViewChecked } from '@angular/core';
import { Observable, of, Subject, throwError } from 'rxjs';
import { HttpStatusCode } from '@angular/common/http';
import { RegistrationRequestsService } from 'src/app/modules/auth/services/registration-requests.service';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { StrangerPermissions } from 'src/app/core/models/stranger-permissions';
import {
  RegistrationRequestDto,
  RegistrationRequestHistoryDto,
  IRegistrationRequestEditFormData,
  IRegistrationRequestStrangerPersonalData,
  IRegistrationRequestStrangerOrganization,
} from 'src/app/core/models/registration-request.model';
import { catchError, map, takeUntil, finalize } from 'rxjs/operators';
import { SearchResult } from '../base-list-table/base-list-table.component';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoService } from '@ngneat/transloco';
import { RegistrationClients } from 'src/app/core/models/registration-clients.model';
import { RegistrationService } from 'src/app/modules/auth/services/registration.service';
import IStrangersType from 'src/app/modules/auth/models/interfaces/IStrangersType';
import { SharedService } from 'src/app/core/services/shared.service';
import { StrangersType } from 'src/app/core/shared/enums/strangersType';
import { IPhoneModel } from 'src/app/core/models/RegisterStrangerRequest';
import { CountriesResponse } from 'src/app/core/models/country';
import { emailRegexp } from 'src/app/core/models/custom-validators.model';
import { NgForm } from '@angular/forms';
import { MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { OkCancelDialogComponent, OkCancelDialogData } from '../ok-cancel-dialog/ok-cancel-dialog.component';
import { getRequestStatusIconName, getRequestStatusName, RegistrationRequestStatus } from 'src/app/core/models/registration-request-status';

const RUSSIAN_COUNTRY_CODE_ID = 643;
const DIALOG_SETTINGS = { width: '500px', enterAnimationDuration: '300ms', exitAnimationDuration: '300ms', panelClass: 'custom-dialog-container' };

export const REGEX_PATTERNS = {
  NAME_PATTERN: /^[a-zA-Zа-яёА-ЯЁ0-9\-–.,()"':\s]+$/,
  INN_PATTERN: /^[a-zA-Z0-9\-–\s]+$/,
  ADDRESS_PATTERN: /^[a-zA-Zа-яёА-ЯЁ0-9\-–.,()"':\s]+$/,
  OLF_PATTERN: /^[a-zA-Zа-яёА-ЯЁ0-9\-–.,()"':\s']+$/,
  ORGANIZATION_NAME_PATTERN: /^[a-zA-Zа-яёА-ЯЁ0-9\-–.,()"':\s?!]+$/,
  EMAIL_PATTERN: emailRegexp,
  ORGANIZATION_ADDRESS_PATTERN: /^[a-zA-Zа-яёА-ЯЁ0-9\-–.,()":\s]+$/,
};
export const VALIDATION_CONSTANTS = {
  MIN_NAME_LENGTH: 2,
  MAX_NAME_LENGTH: 60,
  MAX_INN_LENGTH: 50,
  MAX_ORG_INN_LENGTH: 20,
  MIN_ADDRESS_LENGTH: 5,
  MAX_ADDRESS_LENGTH: 255,
  MIN_EMAIL_LENGTH: 5,
  MAX_EMAIL_LENGTH: 64,
  MAX_ORGANIZATION_NAME_LENGTH: 255,
  MIN_PHONE_LENGTH: 6,
  MAX_PHONE_LENGTH: 64,
};

@Component({
  selector: 'app-registration-request-view-modal',
  templateUrl: './registration-request-view-modal.component.html',
  styleUrls: ['./registration-request-view-modal.component.scss'],
})
export class RegistrationRequestViewModalComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('editForm') editForm!: NgForm;

  requestNumber: string | null = null;
  clientMnemonic: string | null = null;
  registrationRequest: RegistrationRequestDto = {} as RegistrationRequestDto;
  loading = false;
  isDataInitialized = false;

  isEditing = false;
  isClientsErrorShown = false;
  loadingClients = false;

  editData: IRegistrationRequestEditFormData = this.createEmptyEditFormData();
  clients: RegistrationClients[] = [];
  typesUser: IStrangersType[];
  countriesByClients: CountriesResponse[] = [];
  countriesAll: CountriesResponse[] = [];

  registrationRequestHistory: RegistrationRequestHistoryDto[] = [];
  loadingRequestHistory = false;
  registrationRequestHistoryErrorStatus: HttpStatusCode | null = null;
  registrationRequestError: string | null = null;
  isEmptyList = false;
  initialPersonalCountry: CountriesResponse | null = null;
  initialOrgCountry: CountriesResponse | null = null;
  regexPatterns = REGEX_PATTERNS;
  validationConstants = VALIDATION_CONSTANTS;
  private isInitialValidate = false;

  private destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: { request: StrangerPermissions; countriesAll: CountriesResponse[] },
    private registrationRequestsService: RegistrationRequestsService,
    private dialog: MatDialog,
    private translocoService: TranslocoService,
    private registrationService: RegistrationService,
    public sharedService: SharedService,
    private cdr: ChangeDetectorRef,
    private dialogRef: MatDialogRef<RegistrationRequestViewModalComponent>
  ) {
    this.requestNumber = data.request.requestNumber;
    this.clientMnemonic = data.request.clientMnemonic;
    this.countriesAll = data.countriesAll;
    this.registrationRequest.createdAt = data.request.requestedAt;
    this.typesUser = sharedService.getStrangersTypes();
  }

  ngOnInit() {
    this.initializeData();
    this.getRegistrationRequestHistory();
  }

  /** Инициализация всех данных */
  private initializeData() {
    this.loading = true;
    this.registrationRequestError = null;

    this.loadRegistrationRequest()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (request) => {
          this.registrationRequest = request;
          this.initialPersonalCountry = this.findCountryById(this.registrationRequest.countryId, this.countriesAll) || null;
          this.initialOrgCountry = this.findCountryById(this.registrationRequest.orgCountryId, this.countriesAll) || null;
          this.initializeEditData();
          this.isDataInitialized = true;
          this.loading = false;
          this.registrationRequestError = null;
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Ошибка загрузки данных:', error);
          this.isDataInitialized = true;
          this.loading = false;
          this.registrationRequestError = error?.message || 'Произошла неизвестная ошибка при загрузке данных';
          this.cdr.detectChanges();
        },
      });
  }

  /** Загрузка данных заявки */
  private loadRegistrationRequest(): Observable<RegistrationRequestDto> {
    if (this.requestNumber) {
      return this.registrationRequestsService.getUserCard(this.requestNumber, this.clientMnemonic!).pipe(
        catchError((error) => {
          console.error('Ошибка получения данных карточки:', error);
          return throwError(() => error);
        })
      );
    }
    return of({} as RegistrationRequestDto);
  }

  /** Загрузка клиентов */
  private loadClients(): Observable<RegistrationClients[]> {
    return this.registrationService.getRegistrationClients().pipe(
      catchError((error) => {
        console.error('Ошибка загрузки клиентов:', error);
        return of([]);
      })
    );
  }

  /** Загрузка стран для клиентов */
  private loadClientsCountries(mnemonics: string[]): Observable<CountriesResponse[]> {
    return this.registrationService.getStrangerCountries(mnemonics).pipe(
      catchError((error) => {
        console.error('Ошибка загрузки стран:', error);
        return of([]);
      })
    );
  }

  /** Получение данных истории */
  private getHistoryData(): Observable<SearchResult<RegistrationRequestHistoryDto>> {
    if (this.requestNumber) {
      return this.registrationRequestsService.getHistory(this.requestNumber!, this.clientMnemonic!).pipe(
        map((result) => ({
          hasNextPage: false,
          payload: result || [],
          errorMessage: '',
        })),
        catchError((error) => {
          console.error('Ошибка загрузки журнала:', error);
          return throwError(() => error);
        })
      );
    }
    return of({ hasNextPage: false, payload: [], errorMessage: '' });
  }

  /** Загрузка истории */
  getRegistrationRequestHistory() {
    this.loadingRequestHistory = true;
    this.isEmptyList = false;

    this.getHistoryData()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.registrationRequestHistory = result.payload || [];
          this.isEmptyList = this.registrationRequestHistory.length === 0;
          this.loadingRequestHistory = false;
          this.registrationRequestHistoryErrorStatus = null;
        },
        error: (error) => {
          this.registrationRequestHistoryErrorStatus = error.status;
          this.isEmptyList = true;
          this.loadingRequestHistory = false;
        },
      });
  }

  /** Установка данных клиентов */
  private setClientsData(clients: RegistrationClients[], clientId?: number) {
    this.clients = this.mapClientsWithChecked(clients, clientId);
    this.editData.clients = this.cloneClients(this.clients);
  }

  /** Маппинг клиентов */
  private mapClientsWithChecked(clients: RegistrationClients[], clientId?: number): RegistrationClients[] {
    return clients.map((client) => ({
      ...client,
      checked: clientId ? client.id === clientId : false,
    }));
  }

  /** Клонирование клиентов */
  private cloneClients(clients: RegistrationClients[]): RegistrationClients[] {
    return clients.map((client) => ({ ...client }));
  }

  /** Создание пустой формы редактирования */
  private createEmptyEditFormData(): IRegistrationRequestEditFormData {
    return {
      personalData: {} as IRegistrationRequestStrangerPersonalData,
      organizationData: {} as IRegistrationRequestStrangerOrganization,
      phoneOrganization: {
        phoneNumber: '',
        phoneCountryCode: '643',
      } as IPhoneModel,
      clients: [],
      typeUser: null,
    };
  }

  /** Инициализация данных для редактирования */
  private initializeEditData() {
    if (!this.isRequestValid(this.registrationRequest)) return;
    this.editData = this.getInitialRequestDataFormValues();
    this.updateCountryNames();
    this.isClientsErrorShown = false;
  }

  /** Начальные значения формы */
  private getInitialRequestDataFormValues(): IRegistrationRequestEditFormData {
    const typeUser = this.findTypeUser(this.registrationRequest.requesterType.value);
    return this.registrationRequestsService.getInitialEditRequestFormData(
      this.registrationRequest,
      typeUser,
      this.initialPersonalCountry?.name,
      this.initialOrgCountry?.name
    );
  }

  /** Сброс данных редактирования */
  private resetEditData() {
    this.clients = [];
    this.countriesByClients = [];
    this.editData = this.getInitialRequestDataFormValues();
    this.updateCountryNames();
    this.isClientsErrorShown = false;
    this.cdr.detectChanges();
  }

  /** Обновление названий стран */
  private updateCountryNames() {
    const lang = this.getActiveLang();
    const personalCountry = this.findCountryById(this.editData.personalData.countryId, this.countriesAll);
    this.editData.personalData.countryName = personalCountry
      ? this.getCountryNameByLang(personalCountry, lang)
      : this.initialPersonalCountry
        ? this.getCountryNameByLang(this.initialPersonalCountry, lang)
        : '';

    const orgCountry = this.findCountryById(this.editData.organizationData.countryId, [...this.countriesByClients, ...this.countriesAll]);

    this.editData.organizationData.countryName = orgCountry
      ? this.getCountryNameByLang(orgCountry, lang)
      : this.initialOrgCountry
        ? this.getCountryNameByLang(this.initialOrgCountry, lang)
        : '';
  }

  /** Поиск страны по id */
  private findCountryById(id: string | number | undefined, countries: CountriesResponse[]): CountriesResponse | undefined {
    if (!id) return undefined;
    return countries.find((c) => String(c.id) === id);
  }

  /** Название страны по языку */
  private getCountryNameByLang(country: CountriesResponse, lang: string): string {
    return lang === 'ru' ? country.name : country.inEnglish;
  }

  /** Восстановление названий стран из registrationRequest */
  private restoreCountryNamesFromRequest() {
    this.editData.personalData.countryName = this.initialPersonalCountry?.name || '';
    this.editData.organizationData.countryName = this.initialOrgCountry?.name || '';
    this.cdr.detectChanges();
  }

  /** Проверка валидности запроса */
  private isRequestValid(request: RegistrationRequestDto): boolean {
    return request && Object.keys(request).length > 0;
  }

  /** Поиск типа пользователя */
  private findTypeUser(requesterType: string | null): IStrangersType | null {
    if (!requesterType) return null;
    const typeMap: Record<string, string> = {
      [StrangersType.PERSON]: StrangersType.PERSON,
      [StrangersType.ORGANIZATION]: StrangersType.ORGANIZATION,
    };
    const typeId = typeMap[requesterType];
    return typeId ? this.typesUser.find((t) => t.id === typeId) || null : null;
  }

  /** Получение клиентов */
  loadClientsForEdit() {
    if (this.clients.length > 0) {
      this.setClientsData(this.clients, this.registrationRequest.clientId);
      this.loadCountriesForSelectedClients();
      return;
    }

    this.loadingClients = true;
    this.cdr.detectChanges();

    this.loadClients()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loadingClients = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (clients) => {
          this.setClientsData(clients, this.registrationRequest.clientId);
          this.loadCountriesForSelectedClients();
          this.cdr.detectChanges();
        },
        error: () => {
          this.restoreCountryNamesFromRequest();
          this.cdr.detectChanges();
        },
      });
  }

  /** Загрузка стран для выбранных клиентов */
  private loadCountriesForSelectedClients() {
    const selectedMnemonics = this.editData.clients.filter((c) => c.checked).map((c) => c.mnemonic);

    this.loadClientsCountries(selectedMnemonics)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (countries) => {
          this.countriesByClients = countries.length > 0 ? countries : [...this.countriesAll];
          this.updateCountryNames();
          this.cdr.detectChanges();
        },
        error: () => {
          this.countriesByClients = [...this.countriesAll];
          this.restoreCountryNamesFromRequest();
          this.cdr.detectChanges();
        },
      });
  }

  /** Изменение режима редактирования */
  onEditModeChange(value: boolean) {
    this.isEditing = value;
    this.isClientsErrorShown = false;
    if (value) {
      this.isInitialValidate = false;
      this.loadClientsForEdit();
    } else {
      this.resetEditData();
      this.isInitialValidate = false;
    }
    this.cdr.detectChanges();
  }

  ngAfterViewChecked() {
    if (this.isEditing && !this.isInitialValidate) {
      if (this.editForm && Object.keys(this.editForm.controls).length > 0) {
        this.markAllFieldsAsTouched();
        this.isInitialValidate = true;
        this.cdr.detectChanges();
      }
    }
  }

  /** Изменение состояния чекбоксов клиентов */
  onChangeCheckboxClients() {
    this.isClientsErrorShown = false;
    this.loadCountriesForSelectedClients();
  }

  /** Обработка изменения страны */
  onCountryChange(type: 'personal' | 'organization', countryId: string) {
    const countries = type === 'personal' ? this.countriesAll : this.countriesByClients;
    const country = this.findCountryById(countryId, countries);
    const lang = this.getActiveLang();

    if (type === 'personal') {
      this.editData.personalData.countryName = country ? this.getCountryNameByLang(country, lang) : '';
    } else {
      this.editData.organizationData.countryName = country ? this.getCountryNameByLang(country, lang) : '';
    }
    this.cdr.detectChanges();
  }

  /** Сохранить изменения */
  onSaveChanges() {
    if (!this.requestNumber) return;

    const dialogData: OkCancelDialogData = {
      title: this.translocoService.translate('registrationRequests.submitRequest'),
      text: this.translocoService.translate('registrationRequests.reconsiderationText'),
      buttonText: this.translocoService.translate('profileStrangers.send'),
    };

    this.dialog
      .open(OkCancelDialogComponent, { ...DIALOG_SETTINGS, data: dialogData })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: boolean) => {
        if (result) {
          this.performSaveRequest();
        }
      });
  }

  /** Выполнение сохранения и отправки заявки */
  private performSaveRequest() {
    if (!this.requestNumber) return;

    this.loading = true;
    const updatedData = this.registrationRequestsService.getUpdateRequestFormData(this.editData, this.registrationRequest);

    this.registrationRequestsService
      .updateRequest(updatedData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.loading = false;
          this.isEditing = false;
          this.showSuccessModal();
        },
        error: (e) => {
          const error = e;
          this.loading = false;
          console.error('Ошибка отправки заявки:', error);
          if (Array.isArray(e?.error)) {
            error.message = e.error.join('\n');
          }

          this.showErrorModal(error);
        },
      });
  }

  /** Модальное окно успеха */
  private showSuccessModal() {
    this.dialog
      .open(OkCancelDialogComponent, {
        ...DIALOG_SETTINGS,
        data: {
          title: this.translocoService.translate('registrationRequests.successModalTitle'),
          text: this.translocoService.translate('registrationRequests.successModalText'),
          hideConfirmButton: true,
          cancelButtonText: this.translocoService.translate('registrationRequests.close'),
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.dialogRef.close('success');
      });
  }

  /** Модальное окно ошибки */
  private showErrorModal(error: Error) {
    this.dialog.open(OkCancelDialogComponent, {
      ...DIALOG_SETTINGS,
      data: {
        title: this.translocoService.translate('server-error.error'),
        text: `${this.translocoService.translate('registrationRequests.errorModalText')}: ${error?.message}`,
        hideConfirmButton: true,
        cancelButtonText: this.translocoService.translate('registrationRequests.close'),
      },
    });
  }

  isFormInvalid(): boolean {
    if (!this.editForm) return false;
    if (this.editForm.invalid) return true;

    if (this.getCanEditOrganizationData()) {
      const phone = this.editData.phoneOrganization;
      if (!phone?.phoneNumber || phone.phoneNumber.length < VALIDATION_CONSTANTS.MIN_PHONE_LENGTH) {
        return true;
      }
    }

    return false;
  }

  /** Название типа субъекта для отображения */
  getTypeUserName(): string {
    if (!this.registrationRequest.requesterType) return '-';
    const type = this.findTypeUser(this.registrationRequest.requesterType.value);
    return this.getTypeDisplayName(type);
  }

  /** Название типа */
  private getTypeDisplayName(type: IStrangersType | null): string {
    if (!type) return '-';
    return this.getActiveLang() === 'ru' ? type.name : type.inEnglish;
  }

  /** Полное имя человека */
  private getFullPersonName(): string {
    const parts = [this.registrationRequest.surname, this.registrationRequest.firstname, this.registrationRequest.patronymic].filter(Boolean);
    return parts.join(' ') || '';
  }

  /** Название страны для отображения в режиме просмотра */
  getCountryDisplayName(type: 'personal' | 'organization'): string {
    if (type === 'personal') {
      return this.editData.personalData.countryName || this.initialPersonalCountry?.name || '';
    }
    return this.editData.organizationData.countryName || this.initialOrgCountry?.name || '';
  }

  /** Значение id страны */
  getCountryIdString(country: CountriesResponse): string {
    return String(country.id);
  }

  /** Список стран для отображения */
  getCountriesList(list: CountriesResponse[], filterText: string): CountriesResponse[] {
    const isRu = this.getActiveLang() === 'ru';
    return list
      .filter(
        (x) =>
          x.id !== RUSSIAN_COUNTRY_CODE_ID &&
          (x.name.toLocaleLowerCase().includes(filterText.toLowerCase()) || x.inEnglish.toLocaleLowerCase().includes(filterText.toLowerCase()))
      )
      .sort((one, two) => {
        const nameOne = isRu ? one.name : one.inEnglish;
        const nameTwo = isRu ? two.name : two.inEnglish;
        return nameOne < nameTwo ? -1 : 1;
      });
  }

  onChangedOrganizationPhone(phone: IPhoneModel): void {
    this.editData.phoneOrganization.phoneNumber = phone.phoneNumber;
    this.editData.phoneOrganization.phoneCountryCode = phone.phoneCountryCode;
  }

  get getAddress(): string {
    return this.registrationRequest.requesterType.value === StrangersType.PERSON
      ? this.registrationRequest.address || ''
      : this.registrationRequest.orgAddress || '';
  }

  get getRegistrationRequestName(): string {
    if (this.registrationRequest.requesterType.value === StrangersType.PERSON) {
      return this.getFullPersonName();
    }
    return this.registrationRequest.orgFullname || '';
  }

  get isOrganization(): boolean {
    return this.registrationRequest?.requesterType.value === StrangersType.ORGANIZATION;
  }

  get showOrganizationBlock(): boolean {
    if (this.isEditing && this.editData.typeUser) {
      return this.editData.typeUser.id === StrangersType.ORGANIZATION;
    }
    return this.registrationRequest.requesterType.value === StrangersType.ORGANIZATION;
  }

  getActiveLang() {
    return this.translocoService.getActiveLang();
  }

  isClientsSelected(): boolean {
    return this.editData.clients.some((x) => x.checked);
  }

  getUserStatusIcon(status: RegistrationRequestStatus): string {
    return status ? getRequestStatusIconName(status) : '';
  }

  getRequestStatusName(status: RegistrationRequestStatus): string {
    return status ? getRequestStatusName(status, this.translocoService) : '';
  }

  getCanEditPersonalData(): boolean {
    return this.isEditing && this.registrationRequest.canEditPersonalData;
  }

  getCanEditOrganizationData(): boolean {
    return this.isEditing && this.registrationRequest.canEditOrgData;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onPhoneOrganizationChange(phone: IPhoneModel): void {
    this.editData.phoneOrganization = {
      phoneNumber: phone?.phoneNumber || '',
      phoneCountryCode: phone?.phoneCountryCode || '643',
    };
  }

  closeDialog(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  private markAllFieldsAsTouched() {
    if (this.editForm) {
      Object.keys(this.editForm.controls).forEach((key) => {
        const control = this.editForm.controls[key];
        if (control) {
          control.markAsTouched();
        }
      });
    }
  }

  public ValidationConstants = VALIDATION_CONSTANTS;
}
